import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { InternalEnrichedFinding } from '@terra/ui-design-system';

export const FINDING_BY_ID_QUERY_KEY = 'finding-by-id';

export const useInvalidateFindingById = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (findingId?: string | null) => {
      const queryKey = findingId
        ? [FINDING_BY_ID_QUERY_KEY, findingId]
        : [FINDING_BY_ID_QUERY_KEY];
      await queryClient.invalidateQueries({ queryKey, exact: false });
    },
    [queryClient],
  );
};

export const useFindingById = ({
  targetId,
  findingId,
  enabled = true,
}: {
  targetId: string | null | undefined;
  findingId?: string | null;
  enabled?: boolean;
}) => {
  const invalidateById = useInvalidateFindingById();

  const result = useQuery({
    queryKey: [FINDING_BY_ID_QUERY_KEY, findingId, targetId],
    queryFn: async () => {
      if (!findingId) throw new Error('No finding ID provided');
      if (!targetId) throw new Error('No target ID provided');
      const response = await terraProxyApiClient.getFindingById({
        findingId,
        targetId,
      });
      if (response.status !== 200) {
        throw new Error('Failed to fetch finding');
      }
      return response.body as InternalEnrichedFinding;
    },
    enabled: !!findingId && !!targetId && enabled,
  });

  const invalidate = useCallback(() => {
    return invalidateById(findingId);
  }, [invalidateById, findingId]);

  return {
    finding: result.data ?? null,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
    invalidate,
  };
};
